import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  InputAdornment,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Save as SaveIcon,
  Info as InfoIcon,
  LocalHospital as InpatientIcon,
  Science as OncologyIcon,
  MedicalServices as SurgicalIcon,
  Analytics as AnalyticsIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import {
  getFacilitySubscription,
  saveFacilitySubscription,
  getFacilityProviderCount
} from '../../services/facilitySubscriptionService';

const DEFAULT_SUBSCRIPTION = {
  pricingType: 'per_provider',
  flatRateAmount: 0,
  perProviderRate: 100,
  providerCountOverride: null,
  discountPercentage: 0,
  addOns: {
    inpatient: { enabled: false, price: 500 },
    oncology: { enabled: false, price: 750 },
    surgical: { enabled: false, price: 600 },
    analytics: { enabled: false, price: 300 }
  },
  taxRate: 0.08
};

const ADD_ON_CONFIG = [
  { key: 'inpatient', label: 'Inpatient Module', icon: InpatientIcon, description: 'Bed management, admissions, and inpatient workflows' },
  { key: 'oncology', label: 'Oncology Module', icon: OncologyIcon, description: 'Cancer treatment protocols, chemotherapy tracking' },
  { key: 'surgical', label: 'Surgical Module', icon: SurgicalIcon, description: 'Operating room scheduling, surgical workflows' },
  { key: 'analytics', label: 'Analytics Reports', icon: AnalyticsIcon, description: 'Advanced reporting and business intelligence' }
];

const FacilitySubscriptionTab = ({ facilityId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [subscription, setSubscription] = useState(DEFAULT_SUBSCRIPTION);
  const [autoProviderCount, setAutoProviderCount] = useState(0);
  const [loadingProviders, setLoadingProviders] = useState(false);

  // Load subscription data
  const loadSubscription = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const [subData, providerCount] = await Promise.all([
        getFacilitySubscription(facilityId),
        getFacilityProviderCount(facilityId)
      ]);
      
      if (subData) {
        setSubscription({
          ...DEFAULT_SUBSCRIPTION,
          ...subData,
          addOns: {
            ...DEFAULT_SUBSCRIPTION.addOns,
            ...(subData.addOns || {})
          }
        });
      }
      setAutoProviderCount(providerCount || 0);
    } catch (err) {
      console.error('Error loading subscription:', err);
      setError('Failed to load subscription configuration');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  // Refresh provider count
  const refreshProviderCount = async () => {
    setLoadingProviders(true);
    try {
      const count = await getFacilityProviderCount(facilityId);
      setAutoProviderCount(count || 0);
    } catch (err) {
      console.error('Error refreshing provider count:', err);
    } finally {
      setLoadingProviders(false);
    }
  };

  // Get effective provider count
  const effectiveProviderCount = subscription.providerCountOverride !== null && subscription.providerCountOverride !== ''
    ? Number(subscription.providerCountOverride)
    : autoProviderCount;

  // Calculate monthly cost preview
  const costPreview = useMemo(() => {
    let baseAmount = 0;
    
    if (subscription.pricingType === 'flat') {
      baseAmount = Number(subscription.flatRateAmount) || 0;
    } else {
      baseAmount = effectiveProviderCount * (Number(subscription.perProviderRate) || 0);
    }

    // Calculate add-ons total
    const addOnsAmount = Object.entries(subscription.addOns).reduce((total, [key, config]) => {
      if (config.enabled) {
        return total + (Number(config.price) || 0);
      }
      return total;
    }, 0);

    const subtotal = baseAmount + addOnsAmount;
    const discountAmount = subtotal * ((Number(subscription.discountPercentage) || 0) / 100);
    const afterDiscount = subtotal - discountAmount;
    const taxAmount = afterDiscount * (Number(subscription.taxRate) || 0);
    const totalAmount = afterDiscount + taxAmount;

    return {
      baseAmount,
      addOnsAmount,
      subtotal,
      discountAmount,
      afterDiscount,
      taxAmount,
      totalAmount
    };
  }, [subscription, effectiveProviderCount]);

  // Handle field changes
  const handleChange = (field, value) => {
    setSubscription(prev => ({ ...prev, [field]: value }));
  };

  // Handle add-on changes
  const handleAddOnChange = (key, field, value) => {
    setSubscription(prev => ({
      ...prev,
      addOns: {
        ...prev.addOns,
        [key]: {
          ...prev.addOns[key],
          [field]: value
        }
      }
    }));
  };

  // Save subscription
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await saveFacilitySubscription(facilityId, subscription);
      setSuccess('Subscription configuration saved successfully');
    } catch (err) {
      console.error('Error saving subscription:', err);
      setError('Failed to save subscription configuration');
    } finally {
      setSaving(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Loading subscription configuration...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Subscription & Pricing
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure subscription pricing model and add-on services for this facility
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Pricing Model Section */}
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon color="primary" />
                Pricing Model
              </Typography>

              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <RadioGroup
                  value={subscription.pricingType}
                  onChange={(e) => handleChange('pricingType', e.target.value)}
                >
                  {/* Per Provider Option */}
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      mb: 2, 
                      borderColor: subscription.pricingType === 'per_provider' ? 'primary.main' : 'divider',
                      bgcolor: subscription.pricingType === 'per_provider' ? 'primary.50' : 'transparent'
                    }}
                  >
                    <FormControlLabel
                      value="per_provider"
                      control={<Radio />}
                      label={
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          Per Provider Pricing
                        </Typography>
                      }
                    />
                    {subscription.pricingType === 'per_provider' && (
                      <Box sx={{ ml: 4, mt: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Rate per Provider"
                              type="number"
                              value={subscription.perProviderRate}
                              onChange={(e) => handleChange('perProviderRate', e.target.value)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                endAdornment: <InputAdornment position="end">/month</InputAdornment>
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Auto-detected providers:
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {loadingProviders ? <CircularProgress size={16} /> : autoProviderCount}
                              </Typography>
                              <Tooltip title="Refresh provider count">
                                <IconButton size="small" onClick={refreshProviderCount} disabled={loadingProviders}>
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Override Provider Count"
                              type="number"
                              placeholder="Leave empty to use auto-count"
                              value={subscription.providerCountOverride ?? ''}
                              onChange={(e) => handleChange('providerCountOverride', e.target.value === '' ? null : Number(e.target.value))}
                              helperText="Override the automatic provider count if needed"
                              InputProps={{
                                inputProps: { min: 0 }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Effective count: <strong>{effectiveProviderCount}</strong> providers
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </Paper>

                  {/* Flat Rate Option */}
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      borderColor: subscription.pricingType === 'flat' ? 'primary.main' : 'divider',
                      bgcolor: subscription.pricingType === 'flat' ? 'primary.50' : 'transparent'
                    }}
                  >
                    <FormControlLabel
                      value="flat"
                      control={<Radio />}
                      label={
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          Flat Rate Pricing
                        </Typography>
                      }
                    />
                    {subscription.pricingType === 'flat' && (
                      <Box sx={{ ml: 4, mt: 2 }}>
                        <TextField
                          size="small"
                          label="Monthly Amount"
                          type="number"
                          value={subscription.flatRateAmount}
                          onChange={(e) => handleChange('flatRateAmount', e.target.value)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            endAdornment: <InputAdornment position="end">/month</InputAdornment>
                          }}
                          sx={{ width: 250 }}
                        />
                      </Box>
                    )}
                  </Paper>
                </RadioGroup>
              </FormControl>

              {/* Discount */}
              <Box sx={{ mt: 3 }}>
                <TextField
                  size="small"
                  label="Discount"
                  type="number"
                  value={subscription.discountPercentage}
                  onChange={(e) => handleChange('discountPercentage', Math.min(100, Math.max(0, Number(e.target.value))))}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    inputProps: { min: 0, max: 100 }
                  }}
                  helperText="Percentage discount applied to the total"
                  sx={{ width: 200 }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Add-on Services Section */}
          <Card variant="outlined" sx={{ borderRadius: 2, mt: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Add-on Services
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enable optional modules with their monthly pricing
              </Typography>

              <Grid container spacing={2}>
                {ADD_ON_CONFIG.map(({ key, label, icon: Icon, description }) => (
                  <Grid item xs={12} key={key}>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2,
                        borderColor: subscription.addOns[key]?.enabled ? 'success.main' : 'divider',
                        bgcolor: subscription.addOns[key]?.enabled ? 'success.50' : 'transparent'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Icon color={subscription.addOns[key]?.enabled ? 'success' : 'action'} />
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {description}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={subscription.addOns[key]?.price || 0}
                            onChange={(e) => handleAddOnChange(key, 'price', Number(e.target.value))}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                            sx={{ width: 120 }}
                            disabled={!subscription.addOns[key]?.enabled}
                          />
                          <Switch
                            checked={subscription.addOns[key]?.enabled || false}
                            onChange={(e) => handleAddOnChange(key, 'enabled', e.target.checked)}
                            color="success"
                          />
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Tax Configuration */}
          <Card variant="outlined" sx={{ borderRadius: 2, mt: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Tax Configuration
              </Typography>
              <TextField
                size="small"
                label="Tax Rate"
                type="number"
                value={(subscription.taxRate * 100).toFixed(2)}
                onChange={(e) => handleChange('taxRate', Number(e.target.value) / 100)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  inputProps: { min: 0, max: 100, step: 0.01 }
                }}
                helperText="Tax rate applied after discount"
                sx={{ width: 200 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Cost Preview */}
        <Grid item xs={12} md={5}>
          <Card 
            variant="outlined" 
            sx={{ 
              borderRadius: 2, 
              position: 'sticky', 
              top: 20,
              bgcolor: 'grey.50'
            }}
          >
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon color="primary" />
                Monthly Cost Preview
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Base Subscription
                    {subscription.pricingType === 'per_provider' && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({effectiveProviderCount} providers × {formatCurrency(subscription.perProviderRate)})
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="body2">{formatCurrency(costPreview.baseAmount)}</Typography>
                </Box>

                {costPreview.addOnsAmount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Add-on Services</Typography>
                    <Typography variant="body2">{formatCurrency(costPreview.addOnsAmount)}</Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Subtotal</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatCurrency(costPreview.subtotal)}</Typography>
              </Box>

              {costPreview.discountAmount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="success.main">
                    Discount ({subscription.discountPercentage}%)
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    -{formatCurrency(costPreview.discountAmount)}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Tax ({(subscription.taxRate * 100).toFixed(1)}%)
                </Typography>
                <Typography variant="body2">{formatCurrency(costPreview.taxAmount)}</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Monthly Total
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {formatCurrency(costPreview.totalAmount)}
                </Typography>
              </Box>

              {/* Enabled Add-ons List */}
              {Object.entries(subscription.addOns).some(([, config]) => config.enabled) && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Enabled Add-ons:
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {Object.entries(subscription.addOns)
                      .filter(([, config]) => config.enabled)
                      .map(([key, config]) => {
                        const addon = ADD_ON_CONFIG.find(a => a.key === key);
                        return (
                          <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption">{addon?.label}</Typography>
                            <Typography variant="caption">{formatCurrency(config.price)}</Typography>
                          </Box>
                        );
                      })}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FacilitySubscriptionTab;
